var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { FASTElement, attr, observable } from "@microsoft/fast-element";
import { WizardPanelStates } from "./wizard-panel.options.js";
import { WizardPanelEventNames } from "./wizard-panel.options.js";
/**
 * WizardPanel
 * @summary A customizable panel component for a wizard-style interface.
 *
 * @example
 * ```html
 * <fabric-wizard-panel id="step1" state="incomplete" hidden>
 *   <div slot="start">Start Content</div>
 *   <div slot="title">Step 1: Title</div>
 *   <div>Step Content</div>
 *   <div slot="footer">Step Footer</div>
 *   <div slot="end">End Content</div>
 * </fabric-wizard-panel>
 * ```
 *
 * @attr {boolean} hidden - Indicates whether the panel is hidden.
 * @attr {WizardStepState} state - The state of the panel.
 * @attr {boolean} active - Indicates whether the panel is active.
 *
 * @prop {number | undefined} index - The index of the panel in the wizard sequence.
 * @prop {boolean} hidden - Indicates whether the panel is hidden.
 * @prop {WizardStepState} state - The current state of the wizard step.
 * @prop {boolean} active - Indicates whether the panel is currently active.
 *
 * @slot default - The default content of the step.
 * @slot start - The content to display at the start of the step.
 * @slot end - The content to display at the end of the step.
 * @slot title - The content to display as the title of the step.
 * @slot footer - The content to display as the footer of the step.
 *
 * @csspart panel - The root panel container element.
 * @csspart title - The title.
 * @csspart content - The content.
 * @csspart footer - The footer.
 *
 * @method stateChanged - Handles changes to the state attribute.
 * @method activeChanged - Handles changes to the active attribute.
 * @method hiddenChanged - Handles changes to the hidden attribute.
 * @method show - Shows the panel by setting the hidden attribute to false.
 * @method hide - Hides the panel by setting the hidden attribute to true.
 * @method emitChange - Emits a custom event indicating a change in the panel's state.
 *
 * @fires panel-change - Dispatched when the panel state changes.
 *
 * @extends FASTElement
 * @tagname fabric-wizard-panel
 * @public
 */
export class WizardPanel extends FASTElement {
    constructor() {
        super(...arguments);
        /**
         * Indicates whether the panel is hidden.
         * @public
         * @attr
         * @type {boolean}
         * @default true
         */
        this.hidden = true;
        /**
         * The current state of the wizard step.
         * @public
         * @attr
         * @type {WizardPanelState}
         * @default incomplete
         */
        this.state = WizardPanelStates.incomplete;
        /**
         * Indicates whether the panel is currently active.
         * @public
         * @attr
         * @type {boolean}
         * @default false
         */
        this.active = false;
    }
    /**
     * Handles changes to the state attribute.
     * @public
     * @param {WizardPanelState} oldValue - The previous state value.
     * @param {WizardPanelState} newValue - The new state value.
     */
    stateChanged(oldValue, newValue) {
        if (oldValue !== newValue) {
            this.emitChange();
        }
    }
    /**
     * Handles changes to the active attribute.
     * @public
     * @param {boolean} oldValue - The previous active value.
     * @param {boolean} newValue - The new active value.
     */
    activeChanged(oldValue, newValue) {
        if (oldValue !== newValue) {
            if (newValue) {
                this.show();
            }
            else {
                this.hide();
            }
            this.emitChange();
        }
    }
    /**
     * Handles changes to the hidden attribute.
     * @public
     * @param {boolean} oldValue - The previous hidden value.
     * @param {boolean} newValue - The new hidden value.
     */
    hiddenChanged(oldValue, newValue) {
        if (oldValue !== newValue) {
            this.emitChange();
        }
    }
    /**
     * Shows the panel by setting the hidden attribute to false.
     * @public
     */
    show() {
        this.hidden = false;
    }
    /**
     * Hides the panel by setting the hidden attribute to true.
     * @public
     */
    hide() {
        this.hidden = true;
    }
    /**
     * Emits a custom event indicating a change in the panel's state.
     * @public
     */
    emitChange() {
        this.$emit(WizardPanelEventNames.panelChange, {
            id: this.id,
            state: this.state,
            active: this.active,
            index: this.index
        });
    }
}
__decorate([
    observable
], WizardPanel.prototype, "index", void 0);
__decorate([
    attr({ mode: "boolean" })
], WizardPanel.prototype, "hidden", void 0);
__decorate([
    attr
], WizardPanel.prototype, "state", void 0);
__decorate([
    attr({ mode: "boolean" })
], WizardPanel.prototype, "active", void 0);
//# sourceMappingURL=wizard-panel.js.map